import { createAsyncThunk } from "@reduxjs/toolkit";
import axios, { AxiosResponse } from "axios";
import { DEFAULT_ITEMS_PER_PAGE } from "../../constants/app";
import { setTokenAction } from "../auth/slice";
import {
  setCandidateActiveTimesheetList,
  setCandidateLogTimesheetList,
} from "./slice";
import {
  IApprovePayrollRateResponse,
  IExportTimesheetsParams,
  ISendOverdueTimesheetsToPayrollParams,
  ISendOverdueTimesheetsToPayrollResponse,
  ISubmitTimesheetParams,
  TActiveTimeSheetResponse,
  TApprovalPendingResponse,
  TCandidateTimesheetHistoryResponse,
  TGetAllCandidateTimesheetCentresResponse,
  TGetCandidateParams,
  TGetCandidatesResponse,
  TGetCentresParams,
  TLogSubmitTimesheetResponse,
  TPendingTimesheetResponse,
  TTimesheetApprovalParams,
  TTimesheetHistoryParams,
  TTimesheetHistoryResponse,
  TTimesheetPendingParams,
  TTimesheetPendingResponse,
} from "./types";

/**
 * GET method
 * getCandidatesThunk - Fetches candidates for Centre Admin or Super Admin
 */
export const getCandidatesThunk = createAsyncThunk(
  "timesheet/getCandidatesThunk",
  async (data: TGetCandidateParams, { rejectWithValue, dispatch }) => {
    try {
      const response: AxiosResponse<TGetCandidatesResponse> = await axios.get(
        `/v1/timesheet-candidates?timsheet_type=${data.type}${(data.start_date && data.end_date) ? `&start_date=${data.start_date}&end_date=${data.end_date}` : ''}`
      );
      if (response.data?.data?.token) {
        await dispatch(setTokenAction(response.data.data.token));
      }
      return response.data?.data;
    } catch (err) {
      return rejectWithValue((err as any).response?.data || err);
    }
  }
);

/**
 * GET method
 * getCandidatesForExportThunk - Fetches candidates for Centre Admin or Super Admin
 */
export const getCandidatesForExportThunk = createAsyncThunk(
  "timesheet/getCandidatesForExportThunk",
  async (data: TGetCandidateParams, { rejectWithValue, dispatch }) => {
    try {
      const response: AxiosResponse<TGetCandidatesResponse> = await axios.get(
        `/v1/timesheet-candidates?timsheet_type=${data.type}${(data.start_date && data.end_date) ? `&start_date=${data.start_date}&end_date=${data.end_date}` : ''}`
      );
      if (response.data?.data?.token) {
        await dispatch(setTokenAction(response.data.data.token));
      }
      return response.data?.data;
    } catch (err) {
      return rejectWithValue((err as any).response?.data || err);
    }
  }
);

export const getPendingTimesheetThunk = createAsyncThunk(
  "timesheet/getPendingTimesheetThunk",
  async (params: TTimesheetPendingParams, { rejectWithValue, dispatch }) => {
    try {
      const {
        sort_by,
        sort_order,
        searchValue,
        centres,
        candidates,
        page,
        per_page,
      } = params;

      const queryParams = new URLSearchParams();

      if (sort_by) queryParams.append("sort_by", sort_by);
      if (sort_order) queryParams.append("sort_order", sort_order);
      if (searchValue) queryParams.append("keyword", searchValue);
      if (typeof page === "number") queryParams.append("page", String(page));
      if (typeof per_page === "number")
        queryParams.append("per_page", String(per_page));

      const baseUrl = "/v1/pending-timeframes";
      const url = `${baseUrl}?${queryParams.toString()}`;

      // Append raw array-style query strings manually
      const centrePart = centres?.length
        ? `&centre_ids=[${centres.join(",")}]`
        : "";
      const candidatePart = candidates?.length
        ? `&candidate_ids=[${candidates.join(",")}]`
        : "";

      const finalUrl = `${url}${centrePart}${candidatePart}`;
      const response: AxiosResponse<TTimesheetPendingResponse> =
        await axios.get(finalUrl);
      if (response.data?.data?.token) {
        await dispatch(setTokenAction(response.data.data.token));
      }
      await dispatch(getAllCandidateTimesheetCentresThunk({type: 'pending_timeframes'}));
      await dispatch(getCandidatesThunk({type: 'pending_timeframes'}));
      return response.data.data;
    } catch (err) {
      return rejectWithValue((err as any).response.data || err);
    }
  }
);

export const getApprovalTimesheetThunk = createAsyncThunk(
  "timesheet/getApprovalTimesheetThunk",
  async (params: TTimesheetApprovalParams, { rejectWithValue, dispatch }) => {
    try {
      const {
        sort_by,
        sort_order,
        searchValue,
        centres,
        candidates,
        page,
        per_page,
        start_date,
        end_date,
      } = params;

      const queryParams = new URLSearchParams();

      if (sort_by) queryParams.append("sort_by", sort_by);
      if (sort_order) queryParams.append("sort_order", sort_order);
      if (searchValue) queryParams.append("keyword", searchValue);
      if (typeof page === "number") queryParams.append("page", String(page));
      if (typeof per_page === "number")
        queryParams.append("per_page", String(per_page));

      // Only append start_date & end_date if BOTH are present and non-empty
      if (start_date && end_date) {
        queryParams.append("start_date", start_date);
        queryParams.append("end_date", end_date);
      }

      const baseUrl = "/v1/timesheets/pending-approvals";
      const url = `${baseUrl}?${queryParams.toString()}`;

      // Append raw array-style query strings manually
      const centrePart = centres?.length
        ? `&centre_ids=[${centres.join(",")}]`
        : "";
      const candidatePart = candidates?.length
        ? `&candidate_ids=[${candidates.join(",")}]`
        : "";

      const finalUrl = `${url}${centrePart}${candidatePart}`;

      const response: AxiosResponse<TApprovalPendingResponse> = await axios.get(
        finalUrl
      );

      if (response.data?.data?.token) {
        await dispatch(setTokenAction(response.data.data.token));
      }
      await dispatch(getAllCandidateTimesheetCentresThunk({type: 'pending_approvals', start_date, end_date}));
      await dispatch(getCandidatesThunk({type: 'pending_approvals', start_date, end_date}));
      return response.data.data;
    } catch (err) {
      return rejectWithValue((err as any).response.data || err);
    }
  }
);

export const getTimesheetHistoryThunk = createAsyncThunk(
  "timesheet/getTimesheetHistoryThunk",
  async (params: TTimesheetHistoryParams, { rejectWithValue, dispatch }) => {
    try {
      const {
        sort_by,
        sort_order,
        searchValue,
        centres,
        candidates,
        page,
        per_page,
        start_date,
        end_date,
      } = params;

      const queryParams = new URLSearchParams();

      if (sort_by) queryParams.append("sort_by", sort_by);
      if (sort_order) queryParams.append("sort_order", sort_order);
      if (searchValue) queryParams.append("keyword", searchValue);
      if (typeof page === "number") queryParams.append("page", String(page));
      if (typeof per_page === "number")
        queryParams.append("per_page", String(per_page));

      // Only append start_date & end_date if BOTH are present and non-empty
      if (start_date && end_date) {
        queryParams.append("start_date", start_date);
        queryParams.append("end_date", end_date);
      }

      const baseUrl = "/v1/timesheets/history";
      const url = `${baseUrl}?${queryParams.toString()}`;

      // Append raw array-style query strings manually
      const centrePart = centres?.length
        ? `&centre_ids=[${centres.join(",")}]`
        : "";
      const candidatePart = candidates?.length
        ? `&candidate_ids=[${candidates.join(",")}]`
        : "";

      const finalUrl = `${url}${centrePart}${candidatePart}`;

      const response: AxiosResponse<TTimesheetHistoryResponse> =
        await axios.get(finalUrl);

      if (response.data?.data?.token) {
        await dispatch(setTokenAction(response.data.data.token));
      }
      await dispatch(getAllCandidateTimesheetCentresThunk({type: 'history', start_date, end_date}));
      await dispatch(getCandidatesThunk({type: "history", start_date, end_date}));
      return response.data.data;
    } catch (err) {
      return rejectWithValue((err as any).response.data || err);
    }
  }
);

// GET timesheet by ID
export const getTimesheetById = createAsyncThunk(
  "vacancy/getTimesheetById",
  async (id: string, { rejectWithValue, dispatch }) => {
    try {
      const response: AxiosResponse<any> = await axios.get(
        `/v1/timesheets/${id}`
      );
      if (response.data?.data?.token) {
        await dispatch(setTokenAction(response.data.data.token));
      }
      return response.data.data;
    } catch (err) {
      return rejectWithValue((err as any).response.data || err);
    }
  }
);

export const updateTimesheetStatusById = createAsyncThunk(
  "timesheet/updateTimesheetStatusById",
  async (
    {
      id,
      status,
      message,
    }: { id: string; status: "approve" | "decline"; message?: string },
    { rejectWithValue, dispatch }
  ) => {
    try {
      const endpoint = status === "approve" ? "approve" : "decline";
      const payload =
        status === "approve"
          ? { timesheet_id: id }
          : { timesheet_id: id, message };

      const response: AxiosResponse<any> = await axios.post(
        `/v1/timesheets/${endpoint}`,
        payload
      );

      if (response.data?.data?.token) {
        await dispatch(setTokenAction(response.data.data.token));
      }

      return response.data.data;
    } catch (err) {
      return rejectWithValue((err as any).response.data || err);
    }
  }
);

export const exportTimesheetsThunk = createAsyncThunk<
  Blob, // Return the actual file blob
  IExportTimesheetsParams,
  { rejectValue: string }
>("timesheets/exportTimesheets", async (params, { rejectWithValue }) => {
  try {
    const response: AxiosResponse<Blob> = await axios.post(
      "/v1/timesheets/export",
      params,
      {
        responseType: "blob", 
      }
    );

    return response.data; // Return file blob
  } catch (err: any) {
    return rejectWithValue(err?.response?.data || "Export failed");
  }
});

/**
 * GET method
 * getCandidateTimesheetHistoryThunk - To get candidates job details over centres
 */
export const getCandidateTimesheetHistoryThunk = createAsyncThunk(
  "candidates/getCandidateTimesheetHistoryThunk",
  async (
    {
      keyword = "",
      page,
      per_page,
      sort_by = "time_frame",
      sort_order = "desc",
      centre_id,
      start_date,
      end_date,
    }: {
      keyword?: string;
      page?: string | number | null | Array<number>;
      per_page: number;
      sort_by: string;
      sort_order: string;
      centre_id?: string | number | null | Array<number>;
      start_date: string | null;
      end_date: string | null;
    },
    { rejectWithValue, dispatch }
  ) => {
    try {
      // Convert centre_id and role_id to comma-separated strings if they are arrays
      const centreIdParam = Array.isArray(centre_id)
        ? centre_id?.length > 0
          ? "[" + centre_id.join(",") + "]"
          : ""
        : centre_id;

      const response: AxiosResponse<TCandidateTimesheetHistoryResponse> =
        await axios.get(`/v1/timesheets/history`, {
          params: {
            keyword,
            page: page,
            per_page: per_page,
            sort_by: sort_by,
            sort_order: sort_order,
            centre_ids: centreIdParam,
            start_date: start_date,
            end_date: end_date,
          },
        });
      if (response.data?.data?.token) {
        await dispatch(setTokenAction(response.data.data.token));
      }
      await dispatch(getAllCandidateTimesheetCentresThunk({type: 'history', start_date: start_date ? start_date : "", end_date: end_date ? end_date : ""}));
      return response.data;
    } catch (err) {
      return rejectWithValue((err as any).response.data || err);
    }
  }
);

export const getCandidatePendingTimesheetThunk = createAsyncThunk(
  "timesheet/getCandidatePendingTImesheetThunk",
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const response: AxiosResponse<TPendingTimesheetResponse> =
        await axios.get("/v1/pending-timeframes");

      if (response.data?.data?.token) {
        await dispatch(setTokenAction(response.data.data.token));
      }

      const { token, ...timesheetData } = response.data.data;
      await dispatch(setCandidateLogTimesheetList(timesheetData));

      return response.data;
    } catch (err) {
      return rejectWithValue((err as any).response?.data || err);
    }
  }
);

export const submitCandidateTimeSheetThunk = createAsyncThunk(
  "timesheet/submitCandidateTimeSheetThunk",
  async (data: ISubmitTimesheetParams, { dispatch, rejectWithValue }) => {
    try {
      const response: AxiosResponse<TLogSubmitTimesheetResponse> =
        await axios.post(`/v1/timesheets`, data);
      await dispatch(setTokenAction(response.data.data.token));
      return response.data;
    } catch (err) {
      return rejectWithValue((err as any).response.data || err);
    }
  }
);

/**
 * GET method
 * getAllCandidateTimesheetCentresThunk - To get all the centers list which candidate entered timesheet
 */
export const getAllCandidateTimesheetCentresThunk = createAsyncThunk(
  "timesheet/getAllCandidateTimesheetCentresThunk",
  async (data: TGetCentresParams, { rejectWithValue, dispatch }) => {
    try {
      const response: AxiosResponse<TGetAllCandidateTimesheetCentresResponse> =
        await axios.get(`/v1/timesheet-centres?timsheet_type=${data.type}${(data.start_date && data.end_date) ? `&start_date=${data.start_date}&end_date=${data.end_date}` : ''}`);
      await dispatch(setTokenAction(response.data.data.token));
      return response.data;
    } catch (err) {
      return rejectWithValue((err as any).response.data || err);
    }
  }
);

/**
 * GET method
 * getVacancyHistory - Fetch history of  vacancies with optional sorting
 * @queryParams sort_order=asc/desc, sort_by=candidate|timeframe
 */
export const getCandidateActiveTimeSheet = createAsyncThunk(
  "vacancy/getCandidateActiveTimeSheet",
  async (
    {
      page = 1,
      per_page = DEFAULT_ITEMS_PER_PAGE,
      sort_order = "asc",
      sort_by,
    }: {
      page?: string | number | null | Array<number>;
      per_page?: number;
      sort_order: string;
      sort_by?: string;
    },
    { rejectWithValue, dispatch }
  ) => {
    try {
      const response: AxiosResponse<TActiveTimeSheetResponse> = await axios.get(
        "/v1/timesheets/pending-approvals",
        {
          params: {
            page: page || 1,
            per_page: per_page || DEFAULT_ITEMS_PER_PAGE,
            sort_order: sort_order,
            sort_by: sort_by,
          },
        }
      );

      if (response.data?.data?.token) {
        await dispatch(setTokenAction(response.data.data.token));
      }

      if (response.data.data.timesheets) {
        await dispatch(
          setCandidateActiveTimesheetList(response.data.data.timesheets)
        );
      }

      return response.data.data;
    } catch (err) {
      return rejectWithValue((err as any).response?.data || err);
    }
  }
);

export const getPayrolPendingTimesheetThunk = createAsyncThunk(
  "timesheet/getPayrolPendingTimesheetThunk",
  async (params: TTimesheetPendingParams, { rejectWithValue, dispatch }) => {
    try {
      const {
        sort_by,
        sort_order,
        searchValue,
        centres,
        candidates,
        page,
        per_page,
        start_date,
        end_date,
        type
      } = params;

      const queryParams = new URLSearchParams();

      if (sort_by) queryParams.append("sort_by", sort_by);
      if (sort_order) queryParams.append("sort_order", sort_order);
      if (searchValue) queryParams.append("keyword", searchValue);
      if (typeof page === "number") queryParams.append("page", String(page));
      if (typeof per_page === "number")
        queryParams.append("per_page", String(per_page));

      // Only append start_date & end_date if BOTH are present and non-empty
      if (start_date && end_date) {
        queryParams.append("start_date", start_date);
        queryParams.append("end_date", end_date);
      }

      const baseUrl = "/v1/timesheets/payroll-pending";
      const url = `${baseUrl}?${queryParams.toString()}`;

      // Append raw array-style query strings manually
      const centrePart = centres?.length
        ? `&centre_ids=[${centres.join(",")}]`
        : "";
      const candidatePart = candidates?.length
        ? `&candidate_ids=[${candidates.join(",")}]`
        : "";

      const finalUrl = `${url}${centrePart}${candidatePart}`;
      const response: AxiosResponse<TTimesheetPendingResponse> =
        await axios.get(finalUrl);
      if (response.data?.data?.token) {
        await dispatch(setTokenAction(response.data.data.token));
      }
      if(type !== "dashboard") {
        await dispatch(getAllCandidateTimesheetCentresThunk({type: 'payroll_pending', start_date, end_date}));
        await dispatch(getCandidatesThunk({type: 'payroll_pending', start_date, end_date}));
      }
      return response.data.data;
    } catch (err) {
      return rejectWithValue((err as any).response.data || err);
    }
  }
);

export const approvePayRollRate = createAsyncThunk(
  "timesheet/approvePayRollRate",
  async (
    {
      id,
      loading_charge_rate,
      loading_base_rate,
      loading_rate,
      allowance_base_rate,
      allowance_charge_rate
    }: { id: string; message?: string, loading_charge_rate?: number, loading_base_rate?: number, loading_rate?: number, allowance_base_rate: number, allowance_charge_rate: number },
    { rejectWithValue, dispatch }
  ) => {
    try {
     
      const payload = { timesheet_id: id, loading_charge_rate, loading_base_rate, loading_rate, allowance_base_rate, allowance_charge_rate };

      const response: AxiosResponse<IApprovePayrollRateResponse> = await axios.post(
        `/v1/timesheets/approve-payroll`,
        payload
      );

      if (response.data?.data?.token) {
        await dispatch(setTokenAction(response.data.data.token));
      }

      return response.data.data;
    } catch (err) {
      return rejectWithValue((err as any).response.data || err);
    }
  }
);

/**
 * sendOverdueTimesheetsToPayroll
 * Sends overdue timesheets to payroll immediately (Super Admin only)
 */
export const sendOverdueTimesheetsToPayroll = createAsyncThunk(
  "timesheet/sendOverdueTimesheetsToPayroll",
  async (
    params: ISendOverdueTimesheetsToPayrollParams,
    { rejectWithValue, dispatch }
  ) => {
    try {
      const response: AxiosResponse<ISendOverdueTimesheetsToPayrollResponse> = await axios.post(
        `/v1/timesheets/send-to-payroll`,
        params
      );

      if (response.data?.data?.token) {
        await dispatch(setTokenAction(response.data.data.token));
      }

      return response.data;
    } catch (err) {
      return rejectWithValue((err as any).response?.data || err);
    }
  }
);

/**
 * getClientTimesheetPendingApprovalsThunk
 */
export const getClientTimesheetPendingApprovalsThunk = createAsyncThunk(
  "vacancy/getClientTimesheetPendingApprovalsThunk",
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const response: AxiosResponse = await axios.get(
        "/v1/timesheets/pending-approvals?no_paginate=true",
      );

      if (response.data?.data?.token) {
        await dispatch(setTokenAction(response.data.data.token));
      }

      return response.data.data;
    } catch (err) {
      return rejectWithValue((err as any).response?.data || err);
    }
  }
);